import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'

import { type CivilDate } from '~/models/civilDate'
import { now as serverNow } from '~/models/clock'
import { jstYmd } from '~/models/dateTime'
import {
  calendarDate,
  defineFilterCodec,
  oneOf,
  optional,
  protoEnumValues,
} from '~/models/filterCodec'
import {
  SECTION_TABS,
  type SectionTab,
  defaultTarget,
} from '~/pages/SplitSupplyPlan/models/period'

/**
 * 分割供給計画の一覧の絞り込み state (in-memory 形状)。 画面遷移 (一覧 → 詳細 / 取込 → 一覧) や
 * 再訪をまたいで保持する (AC-SC-031)。
 *
 * 保存するのは 断面のタブ・断面ごとの対象日 (翌日は受渡日、 月間は対象月の 1 日)・エリアの絞り込み。
 * 自社の通告値 / 他社の通告値のタブは `?side=` の URL で決まるので含めない。 絞り込みは
 * 自社と他社で共通 (画面が 1 つの state を両方に渡す) なので、 保存も 1 つ。
 */
export type SplitSupplyPlanListFilters = {
  tab: SectionTab
  dayAheadTarget: CivilDate
  monthlyTarget: CivilDate
  area: AreaCode | undefined
}

/** 既定値は今の初期値と同じ: 翌日タブ・明日・来月・エリアの絞り込みなし。 */
export function defaultSplitSupplyPlanListFilters(
  now: Date = serverNow(),
): SplitSupplyPlanListFilters {
  const today = jstYmd(now)
  return {
    tab: 'dayAhead',
    dayAheadTarget: defaultTarget('dayAhead', today),
    monthlyTarget: defaultTarget('monthly', today),
    area: undefined,
  }
}

const pad = (n: number, width: number): string => String(n).padStart(width, '0')

const formatCivil = (d: CivilDate): string =>
  `${pad(d.year, 4)}-${pad(d.month, 2)}-${pad(d.day, 2)}`

/** `YYYY-MM-DD` ↔ CivilDate。 実在しない暦日は往復で一致せず不正になる (`calendarDate`)。 */
const parseCivil = (ymd: string): CivilDate | undefined => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd)
  if (m === null) return undefined
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) }
}

/** 暦日として実在すること (2026-02-31 を 3/3 へ繰り上げて受理しない)。 */
const civilDate = calendarDate<CivilDate>({
  parse: (ymd) => {
    const d = parseCivil(ymd)
    if (d === undefined) return undefined
    const probe = new Date(Date.UTC(d.year, d.month - 1, d.day))
    return probe.getUTCMonth() === d.month - 1 ? d : undefined
  },
  format: formatCivil,
})

/** 月間の対象日は月の 1 日で持つ。 1 日でない保存値は不正 (既定値へ)。 */
const firstOfMonth = calendarDate<CivilDate>({
  parse: (ymd) => {
    const d = civilDate.decode(ymd, { now: serverNow() })
    return typeof d === 'object' && d.day === 1 ? d : undefined
  },
  format: formatCivil,
})

/**
 * 保存形式は `{ tab, dayAheadTarget: 'YYYY-MM-DD', monthlyTarget: 'YYYY-MM-01', area }`。
 * 壊れた値・stale enum・未知フィールドは項目単位で既定へ落ちる。 エリアは絞り込みなしならキーごと落ちる。
 *
 * 対象日は AC-SC-031「再訪時に前回の選択状態を復元する」に従い **無条件に復元** する
 * (過ぎた日の一覧も見られる。 作成・取込の可否は backend が持つ)。
 */
export const splitSupplyPlanListFiltersCodec =
  defineFilterCodec<SplitSupplyPlanListFilters>({
    defaults: defaultSplitSupplyPlanListFilters,
    fields: {
      tab: oneOf(SECTION_TABS),
      dayAheadTarget: civilDate,
      monthlyTarget: firstOfMonth,
      area: optional(oneOf(protoEnumValues(AreaCode))),
    },
  })
